"use client";

import { useAuth } from "@/context/AuthContext";
import { useValidator } from "@/context/ValidatorContext";
import { formatDistanceToNow } from "date-fns";
import { Clock, Loader2, Trash2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { DynamicIcon } from "@/components/ui/DynamicIcon";

export default function RecentIdeas() {
    const { ideas, currentIdea, loadIdea, deleteIdea, loadingIdeas } = useValidator();
    const router = useRouter();
    const { user } = useAuth();

    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const handleSelectIdea = (id: string) => {
        if (confirmDeleteId) return; // Prevent navigation while deleting
        loadIdea(id);
        router.push(`/ai-validator/result?id=${id}`);
    };

    const handleDeleteClick = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setConfirmDeleteId(id);
    };

    const handleConfirmDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await deleteIdea(id);
        setConfirmDeleteId(null);
    };

    const handleCancelDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmDeleteId(null);
    };

    if (loadingIdeas) {
        return (
            <div className="w-full mt-12 flex flex-col items-center justify-center py-8 text-zinc-400">
                <Loader2 className="h-6 w-6 animate-spin mb-2" />
                <span className="text-sm font-medium">Loading past validations...</span>
            </div>
        );
    }

    if (ideas.length === 0) return null;

    return (
        <div className="w-full mt-12 animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
            <div className="flex items-center gap-2 mb-4 px-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Past Validations</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ideas.map((idea) => {
                    const latestResult = idea.history[idea.history.length - 1]?.result;
                    const iconName = latestResult?.lucideIcon;

                    return (
                        <div
                            key={idea.id}
                            onClick={() => handleSelectIdea(idea.id)}
                            className={`group p-4 cursor-pointer transition-all bg-white border rounded-xl flex items-center justify-between gap-4 z-10 relative
                            ${confirmDeleteId === idea.id
                                    ? "border-red-200 bg-red-50/50"
                                    : "border-gray-200 hover:border-black/50 hover:shadow-md"
                                }`}
                        >
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                                <div className={`mt-1 p-2 rounded-lg transition-colors ${confirmDeleteId === idea.id ? "bg-red-100 text-red-600" : "bg-gray-50 group-hover:bg-black group-hover:text-white"}`}>
                                    {confirmDeleteId === idea.id ? <Trash2 className="h-4 w-4" /> : <DynamicIcon name={iconName} className="h-4 w-4" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className={`font-medium line-clamp-2 leading-snug mb-1 ${confirmDeleteId === idea.id ? "text-red-900" : "text-gray-900"}`}>
                                        {idea.description}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border shadow-sm ${confirmDeleteId === idea.id
                                            ? "bg-red-100 text-red-700 border-red-200"
                                            : "bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border-amber-100"
                                            }`}>
                                            {idea.history[idea.history.length - 1]?.result?.score || "N/A"}
                                        </span>
                                        <span className={`text-xs ${confirmDeleteId === idea.id ? "text-red-400" : "text-gray-400"}`}>
                                            {confirmDeleteId === idea.id ? "Delete this idea?" : `${formatDistanceToNow(idea.lastUpdated)} ago`}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {confirmDeleteId === idea.id ? (
                                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4 duration-200 shrink-0 z-20">
                                    <button
                                        onClick={handleCancelDelete}
                                        className="px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={(e) => handleConfirmDelete(e, idea.id)}
                                        className="px-3 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ) : (
                                <div className="shrink-0 z-20">
                                    <button
                                        onClick={(e) => handleDeleteClick(e, idea.id)}
                                        aria-label="Delete idea"
                                        className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                        title="Delete"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
